// @flow

import * as React from 'react';
import {useRef} from 'react';
import Box from 'sail/Box';
import {useContentFrame} from 'docs/shell/content/ContentFrameProvider';
import {
  useContentAnimation,
  variants,
  controlledContentTransition,
} from 'docs/shell/content/ContentAnimationProvider';
import {ContentGroupItem} from 'docs/util';
import type {Props as BoxProps} from 'sail/Box';
import {useAnimation, motion} from 'framer-motion';
import {ContentGroupContext} from './ContentGroupContext';

// $FlowFixMe
export type ControlledContentGroupProps = {|
  contentProps?: BoxProps,
  items: Array<ContentGroupItem>,
  onItemSelected: (item: ContentGroupItem) => void,
  selectedId: ?string,
  selector?: React.Node,
  pref: string,
  // $FlowFixMe
  ...BoxProps,
|};

const getSelectedItem = (
  items: Array<ContentGroupItem>,
  selectedId: ?string,
  previousSelectedId: ?string,
): ContentGroupItem => {
  if (selectedId) {
    const selectedItem = items.find((item) => item.id === selectedId);
    if (selectedItem) {
      return selectedItem;
    }
  }

  // Fall back to the last selected id
  if (previousSelectedId) {
    const selectedItem = items.find((item) => item.id === previousSelectedId);
    if (selectedItem) {
      return selectedItem;
    }
  }

  // Just return the first item
  return items[0];
};

/**
 * A ControlledContentGroup is a ContentGroup that doesn't attempt to control the
 * selected item. Instead, the id of the currently-selected item is passed in from
 * outside via the selectedId prop.
 *
 * This allows the state representing the selected item to be stored somewhere higher
 * in the component tree (such as a Pref), which in turn lets us synchronize the selected
 * item across multiple ControlledContentGroups. For example, we use this to synchronize
 * all CodeTabGroups displayed on a page using a single Pref value.
 *
 * If you don't want to manage the state for the selected item, you should use a
 * UncontrolledContentGroup.
 */
export const ControlledContentGroup = ({
  contentProps = {},
  items,
  onItemSelected,
  selector,
  selectedId,
  pref,
  ...props
}: ControlledContentGroupProps) => {
  const ref = useRef();
  // We keep track of the previously set id to handle cases where
  // we get a selectedId prop that doesn't match any of our
  // items. In this case we "no-op" by falling back to the previously
  // selected item.
  const previousSelectedIdRef = useRef<string | null>(null);
  const {pinAt} = useContentFrame();
  const {animatePref} = useContentAnimation();
  const controls = useAnimation();

  if (items.length === 0) {
    throw new Error('Content groups must contain at least one item');
  }

  const selectedItem = getSelectedItem(
    items,
    selectedId,
    previousSelectedIdRef.current,
  );

  if (animatePref === pref && selectedId !== previousSelectedIdRef.current) {
    // Animate this ContentGroup if it belongs
    // to the pref of the group that is being animated
    const direction =
      items.findIndex((item) => item.id === previousSelectedIdRef.current) >
      items.findIndex((item) => item.id === selectedId);

    const variant = direction ? 'slideRight' : 'slideLeft';
    controls.start(variant);
  }

  previousSelectedIdRef.current = selectedItem.id;

  const handleItemSelected = (item) => {
    if (ref.current) {
      pinAt(ref.current);
    }
    onItemSelected(item);
  };

  const context = {
    items,
    selectedItem,
    onItemSelected: handleItemSelected,
  };

  // TODO(matv): Should this add a key like the uncontrolled content
  // group does?

  return (
    <ContentGroupContext.Provider value={context}>
      <Box {...props} boxRef={ref}>
        {selector}
        <Box {...contentProps} style={{overflow: 'hidden'}}>
          <motion.div
            key="ControlledContentGroup"
            initial="center"
            animate={controls}
            exit="exit"
            variants={variants}
            transition={controlledContentTransition}
          >
            {selectedItem && selectedItem.content}
          </motion.div>
        </Box>
      </Box>
    </ContentGroupContext.Provider>
  );
};
